import KPICards from '../Components/KPICards';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../Components/Table';
import { AlertTriangle } from 'lucide-react';
import {
  kpiCorporateUserData,
  topCorporatesData,
  limitWarningData,
  featureAdoptionData,
} from '../Data/mockData';

export default function CorporateUser() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      
      {/* KPI Cards */}
      <div>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', marginBottom: 16 }}>
          Corporate & User Overview
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
          {kpiCorporateUserData.map((kpi, index) => (
            <KPICards
              key={index}
              label={kpi.label}
              value={kpi.value}
              trend={kpi.trend}
              bgColor={kpi.bgColor}
            />
          ))}
        </div>
      </div>

      {/* Top Corporates by Spend */}
      <div style={{ backgroundColor: '#ffffff', borderRadius: 8, border: '1px solid #e5e7eb', padding: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', marginBottom: 16 }}>
          Top Corporates by Spend
        </h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={topCorporatesData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis type="number" tick={{ fontSize: 12 }} />
            <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={150} />
            <Tooltip formatter={(value) => `AED ${(value / 1000000).toFixed(1)}M`} />
            <Bar dataKey="spend" fill="#3b82f6" name="Monthly Spend" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Corporates Nearing Exposure Limits */}
      <div style={{ backgroundColor: '#ffffff', borderRadius: 8, border: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 24, borderBottom: '1px solid #e5e7eb' }}>
          <AlertTriangle style={{ width: 20, height: 20, color: '#f97316' }} />
          <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827' }}>Corporates Nearing Exposure Limits</h2>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Corporate Name</TableHead>
                <TableHead>Credit Limit</TableHead>
                <TableHead>Utilized</TableHead>
                <TableHead>Utilization %</TableHead>
                <TableHead>Risk Level</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {limitWarningData.map((row, index) => {
                const progressColor = row.percentage >= 90 ? '#dc2626' : '#f97316';
                const rowBgColor = row.percentage >= 90 ? '#fee2e2' : '#ffedd5';
                const riskColor = row.risk === 'High' ? '#b91c1c' : '#c2410c';
                const riskBgColor = row.risk === 'High' ? '#fee2e2' : '#ffedd5';

                return (
                  <TableRow key={index} style={{ backgroundColor: rowBgColor }}>
                    <TableCell style={{ fontWeight: 500 }}>{row.corporate}</TableCell>
                    <TableCell>{row.limit}</TableCell>
                    <TableCell>{row.utilized}</TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, backgroundColor: '#e5e7eb', borderRadius: 9999, height: 8, maxWidth: 100 }}>
                          <div style={{ width: `${row.percentage}%`, backgroundColor: progressColor, height: '100%', borderRadius: 9999 }} />
                        </div>
                        <span style={{ fontWeight: 600 }}>{row.percentage}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span style={{
                        display: 'inline-flex',
                        padding: '2px 8px',
                        fontSize: 12,
                        fontWeight: 500,
                        borderRadius: 9999,
                        color: riskColor,
                        backgroundColor: riskBgColor,
                      }}>
                        {row.risk}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Feature Adoption */}
      <div style={{ backgroundColor: '#ffffff', borderRadius: 8, border: '1px solid #e5e7eb', padding: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', marginBottom: 16 }}>
          Feature Adoption by Corporates
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {featureAdoptionData.map((item, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ width: 160, fontSize: 14, fontWeight: 500, color: '#374151' }}>{item.feature}</div>
              <div style={{ flex: 1, backgroundColor: '#f3f4f6', borderRadius: 9999, height: 32, position: 'relative', overflow: 'hidden' }}>
                <div style={{
                  width: `${item.adoption}%`,
                  background: 'linear-gradient(to right, #3b82f6, #8b5cf6)',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0 16px',
                  color: '#fff',
                  fontWeight: 500,
                  fontSize: 14,
                }}>
                  <span>{item.adoption}%</span>
                  <span style={{ fontSize: 12, opacity: 0.9 }}>{item.corporates} corporates</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* User Statistics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
        {/* Total Users */}
        <div style={{ background: 'linear-gradient(to bottom right, #e0f2fe, #c7d2fe)', borderRadius: 8, border: '1px solid #e5e7eb', padding: 24 }}>
          <div style={{ fontSize: 14, color: '#4b5563', marginBottom: 8 }}>Total Users</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#111827', marginBottom: 16 }}>15,234</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span style={{ color: '#4b5563' }}>Active</span>
              <span style={{ fontWeight: 600, color: '#16a34a' }}>12,890 (84.6%)</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span style={{ color: '#4b5563' }}>Inactive</span>
              <span style={{ fontWeight: 600, color: '#6b7280' }}>2,344 (15.4%)</span>
            </div>
          </div>
        </div>

        {/* Avg Users per Corporate */}
        <div style={{ background: 'linear-gradient(to bottom right, #d1fae5, #a7f3d0)', borderRadius: 8, border: '1px solid #e5e7eb', padding: 24 }}>
          <div style={{ fontSize: 14, color: '#4b5563', marginBottom: 8 }}>Avg Users per Corporate</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#111827', marginBottom: 16 }}>5.4</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span style={{ color: '#4b5563' }}>Median</span>
              <span style={{ fontWeight: 600 }}>4.8</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span style={{ color: '#4b5563' }}>Max</span>
              <span style={{ fontWeight: 600 }}>187</span>
            </div>
          </div>
        </div>

        {/* User Growth */}
        <div style={{ background: 'linear-gradient(to bottom right, #fff7ed, #ffedd5)', borderRadius: 8, border: '1px solid #e5e7eb', padding: 24 }}>
          <div style={{ fontSize: 14, color: '#4b5563', marginBottom: 8 }}>User Growth</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#111827', marginBottom: 16 }}>+15%</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span style={{ color: '#4b5563' }}>This Month</span>
              <span style={{ fontWeight: 600, color: '#16a34a' }}>+345</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span style={{ color: '#4b5563' }}>Last Month</span>
              <span style={{ fontWeight: 600 }}>+298</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
